import { linesForCells } from './constants';
import type { Cell, Constraint, EvaluatedConstraint, Restriction, Tile } from './types';

// ─────────────────────────────────────────────────────────────────────
// Game logic
// ─────────────────────────────────────────────────────────────────────
export function isPrime(n: number): boolean {
  if (!Number.isInteger(n) || n < 2) return false;
  if (n < 4) return true;
  if (n % 2 === 0) return false;
  for (let i = 3; i * i <= n; i += 2) if (n % i === 0) return false;
  return true;
}

export function checkRestriction(value: number | null | undefined, restriction: Restriction | null | undefined): boolean {
  if (restriction == null || value == null) return true;
  switch (restriction) {
    case 'even':  return value % 2 === 0;
    case 'odd':   return Math.abs(value % 2) === 1;
    case 'pos':   return value >= 1;
    case 'neg':   return value <= -1;
    case 'prime': return isPrime(value);
    case 'magHi': return Math.abs(value) >= 5;
    case 'magLo': return Math.abs(value) <= 4;
    default: return true;
  }
}

// Apply a modifier label like "×2" / "+5" / "-1" / "×-" / "²" / "³" / "÷2"
// to a tile value.
export function applyModifier(tileValue: number | null | undefined, modifier: string | null | undefined): number | null | undefined {
  if (modifier == null || tileValue == null) return tileValue;
  // unicode minus and ascii minus both supported
  const norm = modifier.replace(/−/g, '-');
  // Standalone superscript exponents.
  if (norm === '²') return tileValue * tileValue;
  if (norm === '³') return tileValue * tileValue * tileValue;
  // Division — uses the obelus, supports "÷N". Result rounds toward zero so
  // the constraint domain stays integer.
  if (norm.startsWith('÷')) {
    const n = parseInt(norm.slice(1), 10);
    if (!Number.isFinite(n) || n === 0) return tileValue;
    return Math.trunc(tileValue / n);
  }
  const m = norm.match(/^([×x*+\-])(.*)$/);
  if (!m) return tileValue;
  const op = m[1];
  const rest = m[2];
  if (op === '×' || op === 'x' || op === '*') {
    if (rest === '-') return -tileValue;
    const n = parseInt(rest, 10);
    return Number.isFinite(n) ? tileValue * n : tileValue;
  }
  if (op === '+') {
    const n = parseInt(rest, 10);
    return Number.isFinite(n) ? tileValue + n : tileValue;
  }
  if (op === '-') {
    const n = parseInt(rest, 10);
    return Number.isFinite(n) ? tileValue - n : tileValue;
  }
  return tileValue;
}

// Effective value of a cell after modifier
export function effectiveValue(cell: Cell): number | null {
  if (cell.kind === 'locked') return cell.value ?? null;
  if (cell.kind === 'placed') {
    if (cell.modifier != null && cell.value != null) {
      return applyModifier(cell.value, cell.modifier) ?? null;
    }
    return cell.value ?? null;
  }
  return null;
}

// Evaluate a constraint given the values present on the line.
// Returns 'ok' | 'bad' | 'neutral'.
//   ok      = constraint definitely satisfied
//   bad     = constraint definitely violated
//   neutral = not yet decidable
function evalConstraint(c: Constraint, lineCells: { idx: number; eff: number | null }[]): 'ok' | 'bad' | 'neutral' {
  const filled = lineCells.every(x => x.eff != null);
  const vals = lineCells.map(x => x.eff).filter((v): v is number => v != null);

  switch (c.type) {
    case 'sum': {
      const s = vals.reduce((a, b) => a + b, 0);
      if (filled) return s === c.value ? 'ok' : 'bad';
      return 'neutral';
    }
    case 'product': {
      // any zero kills product equality unless target is 0; otherwise always neutral until full
      if (filled) {
        const p = vals.reduce((a, b) => a * b, 1);
        return p === c.value ? 'ok' : 'bad';
      }
      return 'neutral';
    }
    case 'sumGte': {
      if (filled) return vals.reduce((a, b) => a + b, 0) >= (c.value as number) ? 'ok' : 'bad';
      return 'neutral';
    }
    case 'sumLte': {
      if (filled) return vals.reduce((a, b) => a + b, 0) <= (c.value as number) ? 'ok' : 'bad';
      return 'neutral';
    }
    case 'allEven': {
      if (vals.some(x => x % 2 !== 0)) return 'bad';
      return filled ? 'ok' : 'neutral';
    }
    case 'allOdd': {
      if (vals.some(x => Math.abs(x % 2) !== 1)) return 'bad';
      return filled ? 'ok' : 'neutral';
    }
    case 'noNeg': {
      if (vals.some(x => x < 0)) return 'bad';
      return filled ? 'ok' : 'neutral';
    }
    case 'allNeg': {
      if (vals.some(x => x >= 0)) return 'bad';
      return filled ? 'ok' : 'neutral';
    }
    case 'negCount': {
      const negs = vals.filter(x => x < 0).length;
      if (filled) return negs === c.value ? 'ok' : 'bad';
      // overshooting impossible to recover
      if (negs > (c.value as number)) return 'bad';
      return 'neutral';
    }
    case 'contains': {
      const target = new Set((c.value as number[]) || []);
      const present = new Set(vals);
      const haveAll = [...target].every(t => present.has(t));
      if (haveAll) return 'ok';
      return filled ? 'bad' : 'neutral';
    }
    default:
      return 'neutral';
  }
}

// Compute the status of every constraint given current cell state. Gap cells
// are filtered out — they're holes in the grid, not part of any line.
export function computeConstraintStatuses(cells: Cell[], constraints: Constraint[]): EvaluatedConstraint[] {
  const lines = linesForCells(cells.length);
  return constraints.map(c => {
    const lineIdxs = lines[c.axis][c.index].filter(i => cells[i].kind !== 'gap');
    const lineCells = lineIdxs.map(i => ({ idx: i, eff: effectiveValue(cells[i]) }));
    const status = evalConstraint(c, lineCells);
    return { ...c, status };
  });
}

// Restriction violations — cells where placed value doesn't match restriction
export function badRestrictionCells(cells: Cell[]): Set<number> {
  const bad = new Set<number>();
  cells.forEach((c, i) => {
    if (c._restriction && c.kind === 'placed') {
      const eff = effectiveValue(c);
      if (eff != null && !checkRestriction(eff, c._restriction)) bad.add(i);
    }
  });
  return bad;
}

// Compute the live state of a single line (sum, product, neg-count, etc.)
// Used by the banner / chip hover so we can render specific feedback like
// "Column sums to 16, should sum to 18" instead of the generic "doesn't equal 18".
export interface LineStats {
  filled: boolean;
  vals: number[];
  sum: number;
  product: number;
  negCount: number;
  posCount: number;
  lineLength: number;
  hasOdd: boolean;
  hasEven: boolean;
  hasNeg: boolean;
  hasNonNeg: boolean;
}
export function computeLineStats(cells: Cell[], lineIdxs: number[]): LineStats {
  const realIdxs = lineIdxs.filter(i => cells[i].kind !== 'gap');
  const effs = realIdxs.map(i => effectiveValue(cells[i]));
  const filled = effs.every(x => x != null);
  const vals = effs.filter((x): x is number => x != null);
  return {
    filled, vals,
    sum: vals.reduce((a, b) => a + b, 0),
    product: vals.reduce((a, b) => a * b, 1),
    negCount: vals.filter(x => x < 0).length,
    posCount: vals.filter(x => x >= 0).length,
    lineLength: realIdxs.length,
    hasOdd: vals.some(x => Math.abs(x % 2) === 1),
    hasEven: vals.some(x => x % 2 === 0),
    hasNeg: vals.some(x => x < 0),
    hasNonNeg: vals.some(x => x >= 0),
  };
}

// Backtracking solver — assigns remaining bag tiles to remaining empty cells
// such that every constraint is 'ok' and every restriction is satisfied.
// Returns the resulting cells/bag if a solution exists, or null otherwise.
// `timeoutMs` aborts a search that's running too long (defensive — most
// daily puzzles solve in well under 100ms).
export function solveRemaining(
  cells: Cell[],
  bag: Tile[],
  constraints: Constraint[],
  timeoutMs = 1500,
): { cells: Cell[]; bag: Tile[] } | null {
  const emptyIdxs: number[] = [];
  cells.forEach((c, i) => { if (c.kind === 'empty') emptyIdxs.push(i); });
  const unplaced = bag.filter(t => !t.placed);
  if (emptyIdxs.length !== unplaced.length) return null;
  if (emptyIdxs.length === 0) return null;

  const work = cells.map(c => ({ ...c }));
  const used = new Array(unplaced.length).fill(false);
  const start = Date.now();

  const isValidPartial = (): boolean => {
    if (badRestrictionCells(work).size > 0) return false;
    const evals = computeConstraintStatuses(work, constraints);
    return !evals.some(c => c.status === 'bad');
  };

  function backtrack(slotIdx: number): boolean {
    if (Date.now() - start > timeoutMs) return false;
    if (slotIdx === emptyIdxs.length) {
      const evals = computeConstraintStatuses(work, constraints);
      return evals.every(c => c.status === 'ok') && badRestrictionCells(work).size === 0;
    }
    const cellIdx = emptyIdxs[slotIdx];
    const orig = work[cellIdx];
    for (let i = 0; i < unplaced.length; i++) {
      if (used[i]) continue;
      const tile = unplaced[i];
      work[cellIdx] = {
        kind: 'placed',
        value: tile.value,
        placedTileId: tile.id,
        ...(orig.modifier != null ? { modifier: orig.modifier } : {}),
        ...(orig._restriction != null ? { _restriction: orig._restriction } : {}),
      };
      used[i] = true;
      if (isValidPartial() && backtrack(slotIdx + 1)) return true;
      work[cellIdx] = orig;
      used[i] = false;
    }
    return false;
  }

  if (!backtrack(0)) return null;
  const placedIds = new Set(unplaced.map(t => t.id));
  const newBag = bag.map(t => placedIds.has(t.id) ? { ...t, placed: true } : t);
  return { cells: work, bag: newBag };
}

// One actionable hint: either a (cell, bag-tile) pair to place, or a
// placed cell whose tile should be returned to the bag. `null` means
// the state is consistent so far AND the solver couldn't pick a next
// move within budget — caller should treat that as "no hint needed."
export type HintMove =
  | { kind: 'place'; tileId: string; cellIdx: number }
  | { kind: 'undo'; cellIdx: number };

export function findHintMove(
  cells: Cell[],
  bag: Tile[],
  constraints: Constraint[],
): HintMove | null {
  // Forward: solver treats user's placements as locked. If a completion
  // exists, point at the first empty cell the solver fills and the bag
  // tile it'd use.
  const sol = solveRemaining(cells, bag, constraints, 600);
  if (sol) {
    for (let i = 0; i < cells.length; i++) {
      if (cells[i].kind === 'empty' && sol.cells[i].kind === 'placed' && sol.cells[i].placedTileId) {
        return { kind: 'place', tileId: sol.cells[i].placedTileId!, cellIdx: i };
      }
    }
    // Solver found a completion but no empty cell needs a tile — caller
    // already had a complete (and valid) layout. No hint to give.
    return null;
  }

  // Backward: contradictory state. Try removing each user-placed tile;
  // first one that opens a valid completion is the suggested undo. Tight
  // 200ms budget per attempt so worst-case (Expert with all 16 placed)
  // stays bounded.
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    if (c.kind !== 'placed' || !c.placedTileId) continue;
    const tileId = c.placedTileId;
    const trialCells: Cell[] = cells.map((cc, j) => {
      if (j !== i) return cc;
      const empty: Cell = { kind: 'empty' };
      if (cc.modifier != null) empty.modifier = cc.modifier;
      if (cc._restriction != null) empty._restriction = cc._restriction;
      return empty;
    });
    const trialBag: Tile[] = bag.map(t => t.id === tileId ? { ...t, placed: false } : t);
    const sol2 = solveRemaining(trialCells, trialBag, constraints, 200);
    if (sol2) return { kind: 'undo', cellIdx: i };
  }

  return null;
}

// Has the puzzle been solved? All bag tiles placed, all constraints ok, all restrictions ok
export function checkSolved(cells: Cell[], evaluatedConstraints: EvaluatedConstraint[], bag: Tile[]): boolean {
  if (bag.some(t => !t.placed)) return false;
  if (evaluatedConstraints.some(c => c.status !== 'ok')) return false;
  const badRestr = badRestrictionCells(cells);
  if (badRestr.size > 0) return false;
  return true;
}
